import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { LeagueCard } from "@/components/LeagueCard";
import { Button } from "@/components/ui/button";

type Tournament = { id: string; name: string; name_cs?: string | null; name_en?: string | null; is_active: boolean };

/** Routes "/tipy" or "/tabulka" — if user has one active league, redirect; else show picker. */
export const LeagueRedirect = ({ tab }: { tab: "matches" | "table" }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<any[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: mems }, { data: ts }] = await Promise.all([
        supabase.from("league_members").select("league_id").eq("user_id", user.id),
        supabase.from("tournaments").select("id, name, is_active"),
      ]);
      const allTs = (ts as Tournament[]) ?? [];
      setTournaments(allTs);
      const activeIds = new Set(allTs.filter((tr) => tr.is_active !== false).map((tr) => tr.id));
      const ids = (mems ?? []).map((m) => m.league_id);
      if (ids.length === 0) { setLeagues([]); setLoading(false); return; }
      const { data } = await supabase.from("leagues").select("*").in("id", ids);
      const all = data ?? [];
      const active = all.filter((l: any) => !l.tournament_id || activeIds.has(l.tournament_id));
      const archived = all.filter((l: any) => !!l.tournament_id && !activeIds.has(l.tournament_id));
      // Only auto-redirect when there is exactly one active league and no archived
      // leagues to choose from. Otherwise show the picker so the archive toggle
      // can be used (e.g. on /standings).
      if (active.length === 1 && archived.length === 0) {
        navigate(`/league/${active[0].id}?tab=${tab}`, { replace: true });
        return;
      }
      setLeagues(all);
      setLoading(false);
    })();
  }, [user, tab, navigate]);

  const activeTournamentIds = useMemo(
    () => new Set(tournaments.filter((tr) => tr.is_active !== false).map((tr) => tr.id)),
    [tournaments],
  );
  const archivedLeagues = useMemo(
    () => leagues.filter((l) => !!l.tournament_id && !activeTournamentIds.has(l.tournament_id)),
    [leagues, activeTournamentIds],
  );
  const activeLeagues = useMemo(
    () => leagues.filter((l) => !archivedLeagues.some((a) => a.id === l.id)),
    [leagues, archivedLeagues],
  );
  const visibleLeagues = showArchived ? archivedLeagues : activeLeagues;

  if (loading) return <AppShell><p className="text-muted-foreground">{t("common.loading")}</p></AppShell>;
  return (
    <AppShell title={t(tab === "matches" ? "nav.matches" : "nav.table")}>
      <h1 className="text-2xl font-bold mb-2">{t(tab === "matches" ? "nav.matches" : "nav.table")}</h1>
      <p className="text-muted-foreground text-sm mb-4">{t("league.pickLeague")}</p>

      {archivedLeagues.length > 0 && (
        <div className="mb-4 flex items-center justify-end">
          <Button variant="outline" size="sm" onClick={() => setShowArchived((v) => !v)}>
            {showArchived
              ? t("dashboard.showActive")
              : t("dashboard.showArchived", { count: archivedLeagues.length })}
          </Button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {visibleLeagues.length === 0 && (
          <p className="text-muted-foreground text-sm sm:col-span-2">
            {showArchived ? t("dashboard.noArchived") : t("dashboard.noLeague")}
          </p>
        )}
        {visibleLeagues.map((l) => (
          <div key={l.id} onClick={() => navigate(`/league/${l.id}?tab=${tab}`)}>
            <LeagueCard
              id={l.id}
              name={l.name}
              invite_code={l.invite_code}
              isAdmin={l.owner_id === user?.id}
              isPremium={l.is_premium}
              logo_url={l.logo_url}
              tournamentName={(() => {
                const tr = tournaments.find((tr) => tr.id === l.tournament_id);
                return tr ? ((i18n.language?.startsWith("en") ? tr.name_en : tr.name_cs) || tr.name) : undefined;
              })()}
            />
          </div>
        ))}
      </div>
    </AppShell>
  );
};
